import { useQuestionBookableResources } from '~/components/forms/questions/scheduling/SelectBookableResource';
import Calendar from 'react-calendar';
import { useState } from 'react';
import Tooltip from '~/components/Tooltip';
import { useOverviewQuery } from '~/lib/SharedQueries';
import {
    addDays,
    addMonths,
    endOfDay,
    endOfMonth,
    format,
    isPast,
    isSameDay,
    isSameMonth,
    startOfDay,
    startOfMonth,
    subDays,
    subMonths,
} from 'date-fns';
import Loading from '~/components/Loading';
import LoadingLayout from '~/components/LoadingLayout';

const MonthlyOverview = ({ settings, setFilter }) => {
    const [selectedDate, setSelectedDate] = useState(null);
    const [viewActiveDate, setViewActiveDate] = useState(new Date());
    const [view, setView] = useState();
    const {
        isLoading: isLoadingBookableResources,
        bookableResources: allBookableResources,
    } = useQuestionBookableResources(null, null, settings);

    const { data, isLoading } = useOverviewQuery(
        startOfDay(startOfMonth(viewActiveDate)),
        endOfDay(endOfMonth(viewActiveDate)),
        allBookableResources,
    );

    const resourcesByDate = data || {};

    /**
     * Get the bookable resources (objects) given the day
     * checks if same day and maps that elements ids to objects
     *
     * @param date
     */
    function getResourcesForDate(date) {
        for (let k in resourcesByDate) {
            if (isSameDay(date, new Date(k))) {
                // Found it! Now map it to real objects
                return resourcesByDate[k].map(id =>
                    allBookableResources.find(
                        br => parseInt(id) === parseInt(br.id),
                    ),
                );
            }
        }

        return [];
    }

    function getGroupedResources(date) {
        const resources = getResourcesForDate(date);
        const groups = {};
        for (let br of resources) {
            let groupName = '';
            console.log(
                'Settings to group by',
                settings?.overviewGroup,
                'fields',
                br.custom_fields,
            );
            if (settings?.overviewGroup && br.custom_fields) {
                groupName = br.custom_fields[settings?.overviewGroup];
            }

            // Fallback to resource name if field not set
            if (!groupName) {
                groupName = br.name;
            }

            groups[groupName] = [...(groups[groupName] || []), br];
        }

        return groups;
    }

    const month = startOfMonth(viewActiveDate);
    const weekStart = 0; // 0 = Sunday, 1 = Monday
    const monthlyCalendar = [];
    const monthlyViewStart = subDays(month, month.getDay() - weekStart);
    const monthViewEnd = addDays(monthlyViewStart, 5 * 7); // Always display 5 weeks
    for (let week = 0; week < 5; week++) {
        let weekdays = [];
        for (let dow = weekStart; dow < 7 + weekStart; dow++) {
            weekdays.push(addDays(monthlyViewStart, week * 7 + dow));
        }
        monthlyCalendar.push(weekdays);
    }

    const container = document.getElementsByClassName(
        'monthly-overview-container',
    )[0];
    const dayWidth = container
        ? container.getBoundingClientRect().width / 7
        : 100;

    return (
        <div className="monthly-overview-container max-w-2xl relative">
            {isLoading && <LoadingLayout isLoading={isLoading} />}
            <div className="flex items-center justify-center mb-1">
                <button
                    type="button"
                    className="themed-text px-4 pb-1"
                    onClick={() =>
                        setViewActiveDate(subMonths(viewActiveDate, 1))
                    }
                >
                    ‹
                </button>
                <div className="text-lg">{format(month, 'LLLL yyyy')}</div>
                <button
                    type="button"
                    className="themed-text px-4 pb-1"
                    onClick={() =>
                        setViewActiveDate(addMonths(viewActiveDate, 1))
                    }
                >
                    ›
                </button>
            </div>

            <div className="flex justify-between">
                {monthlyCalendar[0].map(dow => (
                    <div
                        key={dow.toString()}
                        className="flex-1 p-1 text-gray-500 text-sm uppercase"
                    >
                        {format(dow, 'E')}
                    </div>
                ))}
            </div>
            <div
                className={`panel border-t border-l overflow-hidden ${isLoading ? 'opacity-75' : ''}`}
            >
                {monthlyCalendar.map(weekdays => (
                    <div className="flex justify-between border-b">
                        {weekdays.map(date => (
                            <div
                                key={date.toString()}
                                className={`min-h-16 text-wrap overflow-hidden border-r p-1 ${isPast(date) ? 'opacity-50' : 'cursor-pointer hover:bg-gray-100'} ${isSameDay(date, selectedDate) ? 'themed-bg-light' : ''}`}
                                onClick={() => {
                                    if (isPast(date)) {
                                        return;
                                    }
                                    setSelectedDate(date);
                                    setFilter({
                                        date: date,
                                    });
                                }}
                                style={{
                                    width: `${dayWidth}px`,
                                }}
                            >
                                <div
                                    className={`text-xs ${isSameMonth(date, month) ? 'text-gray-500' : 'text-gray-400'}`}
                                >
                                    {format(date, 'd')}
                                </div>
                                <div className="text-xs mt-1">
                                    {Object.keys(getGroupedResources(date)).map(
                                        name => (
                                            <div className="leading-none mb-1">
                                                {name}
                                            </div>
                                        ),
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default MonthlyOverview;
